import { useState } from "react";
import {
  ColScaffold,
  RowScaffold,
  Scaffold,
  ScaffoldDropZone,
  ScaffoldZoneColors,
} from "../../scaffold";
import { isArsSidebarItemData } from "../sidebar";
import { ArsDraggableSidebar } from "../sidebar/ArsDraggableSidebar";
import { ArsSidebarItemData } from "../sidebar/interface";
import { ArsTableCellEditorProps } from "./ArsTableCell";
import { ArsTableContextMenuProvider } from "./ArsTableContextMenuProvider";
import { useArsTable } from "./ArsTableContextProvider";
import { ArsTableItemProperties } from "./ArsTableItemProperties";
import { ArsTableTreeView } from "./ArsTableTreeView";
import { cellActionConfig } from "./cellActionConfig";
import { buildArsTableZoneColors } from "./functions";
import {
  ARST_SET_SCAFFOLD_COL_WIDTH,
  ARST_SET_SCAFFOLD_ROW_HEIGHT,
  ArsTableCellData,
} from "./interface";

/**
 * Renders ArsTable scaffold content and handles scaffold interactions.
 *
 * @returns ArsTable scaffold and draggable sidebar
 */
export const ArsTableContent = <T,>(): React.ReactNode => {
  const { state, dispatch } = useArsTable<T>();
  const disabled = state?.settings.disabled;
  const justification = state?.settings.justification;
  const scaffoldModel = state?.scaffoldModel;
  const [dragItem, setDragItem] = useState<ArsSidebarItemData[] | null>(null);

  // Return if things are missing
  if (!state || !scaffoldModel) return null;

  const zoneColors: ScaffoldZoneColors = buildArsTableZoneColors(
    state,
    dragItem?.[0]?.kind ?? null,
  );

  /**
   * Handles scaffold drop events by building and dispatching table-drop actions.
   *
   * @param event - Drop event
   * @param rowId - Target row id
   * @param colId - Target column id
   * @param zone - Drop zone within the target cell
   */
  const handleScaffoldDrop = (
    event: React.DragEvent<HTMLDivElement>,
    rowId: string,
    colId: string,
    zone: ScaffoldDropZone,
  ): void => {
    if (!disabled && state && state.scaffoldModel) {
      event.preventDefault();
      try {
        // Parse dropped data from the drag event
        const jsonPayload: { data: unknown[] } = JSON.parse(
          event.dataTransfer.getData("application/json"),
        );
        // Validate the parsed data
        if (jsonPayload.data.every(isArsSidebarItemData)) {
          const payload = jsonPayload.data as ArsSidebarItemData[];
          // Check all payload items are the same kind
          const firstKind = payload[0].kind;
          if (!payload.every((item) => item.kind === firstKind)) {
            return;
          }
          // Get target cell data
          const targetCell: ArsTableCellData<T> | undefined =
            state.scaffoldModel.cellData?.[rowId]?.[colId];
          // Now we have enough information to create the action payload
          if (targetCell) {
            // Get the cell config for the target cell type
            const cellConfig = cellActionConfig(targetCell, state);
            // Get the action for this zone & dropped data
            const matchedDropAction = cellConfig?.dropActions.find(
              (action) => action.dropZones.has(zone) && action.droppedDataKinds.includes(firstKind),
            );
            if (matchedDropAction) {
              dispatch({
                operation: matchedDropAction.operation,
                rowId,
                columnId: colId,
                payload,
                zone,
              });
            }
          }
        }
      } catch {
        return;
      }
    }
  };

  /**
   * Handles scaffold column resize updates.
   *
   * @param colId - Resized column id
   * @param col - Updated scaffold column definition
   */
  const handleChangeCol = (colId: string, col: ColScaffold<ArsTableCellData<T>>): void => {
    if (!disabled) {
      dispatch({
        operation: ARST_SET_SCAFFOLD_COL_WIDTH,
        colId,
        width: col.width,
      });
    }
  };

  /**
   * Handles scaffold row resize updates.
   *
   * @param rowId - Resized row id
   * @param row - Updated scaffold row definition
   */
  const handleChangeRow = (rowId: string, row: RowScaffold<ArsTableCellData<T>>): void => {
    if (!disabled) {
      dispatch({
        operation: ARST_SET_SCAFFOLD_ROW_HEIGHT,
        rowId,
        height: row.height,
      });
    }
  };

  return (
    <>
      <div
        className="d-flex flex-row"
        style={{ width: "100%", minWidth: 0, minHeight: "500px" }}
      >
        <div
          className="flex-grow-1"
          style={{ minWidth: 0 }}
        >
          {state.settings.tlfViewMode.value === "ARS_VIEW" ? (
            <ArsTableTreeView />
          ) : (
            <ArsTableContextMenuProvider>
              <Scaffold<ArsTableCellData<T>>
                id={`ars-table-scaffold-${state.settings.id}`}
                cellComponent={ArsTableCellEditorProps(state.settings.editorProps)}
                cellData={scaffoldModel.cellData}
                columns={scaffoldModel.columns}
                disabled={disabled}
                headerCellSpans={scaffoldModel.headerColSpans}
                justification={justification}
                onChangeCol={handleChangeCol}
                onChangeRow={handleChangeRow}
                onDrop={handleScaffoldDrop}
                rows={scaffoldModel.rows}
                style={state.settings.style}
                zoneColors={zoneColors}
              />
            </ArsTableContextMenuProvider>
          )}
        </div>
      </div>
      <ArsTableItemProperties />
      {!disabled && (
        <ArsDraggableSidebar
          onItemDragStart={(items) => setDragItem(items.map((item) => item.data))}
          onItemDragEnd={() => setDragItem(null)}
          showGroupingFactors
          showOperations
          showTotalGroupingFactor
          style={state.settings.sidebarStyle}
        />
      )}
    </>
  );
};

ArsTableContent.displayName = "ArsTableContent";
